import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransactions } from '../slices/billingSlice';
import {
  HiOutlineArrowDown,
  HiOutlineArrowUp,
  HiOutlineRefresh,
  HiOutlineLightningBolt,
  HiOutlineCalendar,
  HiOutlineDocumentText,
} from 'react-icons/hi';

const TX_CONFIG = {
  usage: {
    label: 'Usage',
    icon: HiOutlineArrowDown,
    badge: 'badge badge-error',
    amount: 'text-red-500',
  },
  purchase: {
    label: 'Purchase',
    icon: HiOutlineArrowUp,
    badge: 'badge badge-success',
    amount: 'text-emerald-600',
  },
  reset: {
    label: 'Reset',
    icon: HiOutlineRefresh,
    badge: 'badge badge-info',
    amount: 'text-brand-500',
  },
};

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-5 py-4 animate-pulse">
      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-2/5" />
        <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/4" />
      </div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-14" />
    </div>
  );
}

export default function Credits() {
  const dispatch = useDispatch();

  const { transactions = [], loading } = useSelector((s) => s.billing || {});
  const user = useSelector((s) => s.auth?.user);

  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  const totalUsed = transactions
    .filter((t) => t.type === 'usage')
    .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

  return (
    <div className="animate-fade-in max-w-4xl space-y-6">
      <div>
        <h1 className="page-title">Credit History</h1>
        <p className="page-subtitle">
          Track your credit usage, purchases, and monthly resets
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-brand-100 dark:bg-brand-900/30 text-brand-600 flex items-center justify-center shrink-0">
            <HiOutlineLightningBolt size={22} />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Available Credits</p>
            <p className="text-2xl font-bold text-brand-600 mt-1">
              {user?.credits?.toLocaleString() ?? 0}
            </p>
          </div>
        </div>

        <div className="card p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-500 flex items-center justify-center shrink-0">
            <HiOutlineArrowDown size={22} />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Used</p>
            <p className="text-2xl font-bold mt-1">{totalUsed.toLocaleString()}</p>
          </div>
        </div>

        <div className="card p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-500 flex items-center justify-center shrink-0">
            <HiOutlineCalendar size={22} />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Next Reset</p>
            <p className="text-base font-semibold">
              {user?.creditsResetDate
                ? new Date(user.creditsResetDate).toLocaleDateString()
                : '—'}
            </p>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-2">
            <HiOutlineDocumentText size={16} className="text-gray-500 dark:text-gray-400" />
            <h3 className="font-semibold text-sm">Transactions</h3>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">{transactions.length} records</span>
        </div>

        {loading ? (
          <div className="divide-y">
            {[...Array(5)].map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <HiOutlineLightningBolt size={28} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No transactions yet</p>
            <span className="text-xs text-gray-500 dark:text-gray-400">Activity will appear here once usage begins.</span>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {transactions.map((tx) => {
              const cfg = TX_CONFIG[tx.type] || TX_CONFIG.reset;
              const Icon = cfg.icon;

              return (
                <div key={tx._id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                      tx.type === 'usage'
                        ? 'bg-red-100 text-red-500'
                        : tx.type === 'purchase'
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-blue-100 text-blue-500'
                    }`}
                  >
                    <Icon size={15} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {tx.description || cfg.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(tx.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <span className={`text-sm font-semibold ${cfg.amount}`}>
                    {tx.amount > 0 ? '+' : ''}
                    {(tx.amount || 0).toLocaleString()}
                  </span>

                  <span className={cfg.badge}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

